/**
 * Dynamic Theme Provider that updates theme based on language direction
 * Uses Zustand store for language state management
 */
import React, {useMemo} from 'react'

import createCache from '@emotion/cache'
import {CacheProvider} from '@emotion/react'
import {ThemeProvider} from '@mui/material/styles'
import rtlPlugin from '@mui/stylis-plugin-rtl'
import {prefixer} from 'stylis'

import {useLanguageStore} from '@progyny/store/languageStore'

import {createDirectionalTheme} from '@root/theme'

interface DynamicThemeProviderProps {
  children: React.ReactNode
}

export const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({
  children,
}) => {
  // Use Zustand store directly - it will only re-render when these specific values change
  const defaultLanguage = useLanguageStore((state) => state.defaultLanguage)
  const isRTL = useLanguageStore((state) => state.isRTL)

  // Create theme based on current language direction
  const theme = useMemo(() => {
    return createDirectionalTheme(defaultLanguage || 'en')
  }, [defaultLanguage])

  // Create emotion cache based on RTL state
  const emotionCache = useMemo(() => {
    if (isRTL) {
      // RTL cache with stylis RTL plugin
      return createCache({
        key: 'muirtl',
        stylisPlugins: [prefixer, rtlPlugin],
      })
    } else {
      // LTR cache (default)
      return createCache({
        key: 'muiltr',
        stylisPlugins: [prefixer],
      })
    }
  }, [isRTL])

  // Key forces remount when direction changes so RTL-injected styles are discarded and LTR styles apply correctly
  const directionKey = isRTL ? 'rtl' : 'ltr'

  return (
    <CacheProvider key={directionKey} value={emotionCache}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </CacheProvider>
  )
}

export default DynamicThemeProvider
